import { useMemo } from 'react';
import type { TextOnPathProps } from '../../TextOnPath.types';
import type { Measurement } from '../useSvgMeasurement';

type UseTextOnPathCalculationsProps = Pick<
  TextOnPathProps,
  'fillMode' | 'pathLength' | 'movementDirection' | 'speed' | 'text'
> & {
  measurement?: Measurement;
  startOffset?: number;
};

/**
 * Calculates pixel-based animation offsets for text movement along a path.
 *
 * @param movementDirection - Direction of text movement ('forward' for left-to-right, 'reverse' for right-to-left)
 * @param biggestLength - The largest dimension between text width and path length in pixels
 * @param pathLength - Total length of the path in pixels
 * @param effectiveStartOffset - Starting position as a percentage (0-100) of the path length
 * @returns Object containing pixel-based animation offset values (from1, to1, from2, to2) as strings
 */
function getAnimationOffsets(
  movementDirection: TextOnPathProps['movementDirection'],
  biggestLength: number,
  pathLength: number,
  effectiveStartOffset: number,
) {
  // Calculate pixel offset from percentage-based startOffset
  const pixelOffset = (effectiveStartOffset / 100) * pathLength;

  // If text is moving left-to-right
  if (movementDirection === 'forward') {
    return {
      from1: `${-biggestLength + pixelOffset}`,
      to1: `${pixelOffset}`,
      from2: `${pixelOffset}`,
      to2: `${biggestLength + pixelOffset}`,
    };
  }

  // If text is moving right-to-left
  return {
    from1: `${pixelOffset}`,
    to1: `${-biggestLength + pixelOffset}`,
    from2: `${biggestLength + pixelOffset}`,
    to2: `${pixelOffset}`,
  };
}

export function useTextOnPathCalculations({
  fillMode,
  pathLength,
  measurement,
  movementDirection,
  speed,
  text,
  startOffset = 0,
}: UseTextOnPathCalculationsProps) {
  const isHydrated = !!measurement;
  const isFillMode = fillMode === 'repeat';
  const isJustifyMode = fillMode === 'justify';

  const effectiveStartOffset = fillMode === 'normal' ? startOffset : 0;

  // Calculate repetitions for fill mode using pathLength prop
  const fallbackRepetitions = isFillMode ? 10 : 1;
  const repetitions =
    isHydrated && isFillMode && pathLength && measurement?.textWidth
      ? Math.ceil(pathLength / measurement.textWidth)
      : fallbackRepetitions;

  const repeatedText = repetitions > 1 ? `${text} `.repeat(repetitions) : text;

  const { textWidthMultiplied, animationOffsets, duration } = useMemo(() => {
    let calculatedTextWidth: number | undefined;
    if (isJustifyMode) {
      calculatedTextWidth = pathLength;
    } else if (measurement?.textWidth) {
      calculatedTextWidth = measurement.textWidth * repetitions;
    }
    const calculatedLength = Math.max(
      calculatedTextWidth || 0,
      pathLength || 0,
    );
    const calculatedOffsets = getAnimationOffsets(
      movementDirection,
      calculatedLength,
      pathLength || 0,
      effectiveStartOffset,
    );
    const distance = calculatedLength || 0;
    const calculatedDuration = speed === 0 ? -1 : distance / (speed * 10);

    return {
      textWidthMultiplied: calculatedTextWidth,
      animationOffsets: calculatedOffsets,
      duration: calculatedDuration,
    };
  }, [
    isJustifyMode,
    pathLength,
    measurement?.textWidth,
    repetitions,
    movementDirection,
    speed,
    effectiveStartOffset,
  ]);

  return {
    textWidthMultiplied,
    animationOffsets,
    duration,
    repeatedText,
    effectiveStartOffset,
  };
}
